import type {
  KeyFingerprints,
  MailTestStage,
  SendgridKeyStatus,
  SmtpConfig,
} from "@/services/mailDiagnosticApi";

export const STAGE_LABELS: Record<MailTestStage["stage"], string> = {
  config: "Configuration",
  connect: "Connexion",
  tls: "Chiffrement TLS",
  auth: "Authentification (jeton)",
  send: "Envoi",
};

export type StageStatus = "ok" | "failed" | "not_applicable" | "not_reached";

/** A skipped stage is fine when not applicable, a symptom when not reached. */
export function stageStatus(stage: MailTestStage): StageStatus {
  if (stage.skipped) return stage.ok ? "not_applicable" : "not_reached";
  return stage.ok ? "ok" : "failed";
}

export const STAGE_STATUS_BADGE: Record<StageStatus, { label: string; className: string }> = {
  ok: { label: "OK", className: "bg-green-100 text-green-800 border-green-200" },
  failed: { label: "Échec", className: "bg-red-100 text-red-800 border-red-200" },
  not_applicable: {
    label: "Non applicable",
    className: "bg-gray-100 text-gray-700 border-gray-200",
  },
  not_reached: { label: "Non exécuté", className: "bg-gray-100 text-gray-500 border-gray-200" },
};

const OUTCOME_LABELS: Record<string, string> = {
  ok: "Jeton accepté par le relais",
  sent: "E-mail accepté par le relais",
  auth_failed: "Jeton refusé par le relais (AUTH)",
  network_failed: "Relais injoignable (réseau ou TLS)",
  rejected: "Message refusé par le relais",
  not_configured: "SMTP non configuré",
  credentials_missing: "Identifiants SMTP incomplets",
  failed: "Échec inattendu",
};

export function outcomeLabel(outcome: string): string {
  return OUTCOME_LABELS[outcome] ?? outcome;
}

const OUTCOME_HINTS: Record<string, string> = {
  auth_failed:
    "La clé SendGrid de SMTP_PASSWORD est révoquée, expirée ou fausse (code 451 : crédits " +
    "SendGrid épuisés). Suivre internal-docs/runbooks/sendgrid-key-rotation.md.",
  network_failed:
    "Vérifier SMTP_HOST, SMTP_PORT et le mode TLS (SMTP_USE_SSL / SMTP_STARTTLS), puis l'état " +
    "de SendGrid.",
  rejected:
    "Le relais a accepté la clé mais refuse ce message : expéditeur (SMTP_FROM doit être une " +
    "identité vérifiée chez SendGrid) ou destinataire.",
  not_configured: "SMTP_HOST et SMTP_FROM (ou SMTP_USER) doivent être posés côté back.",
  credentials_missing: "SMTP_USER est posé sans SMTP_PASSWORD : vérifier le secret Infisical /app.",
  failed: "Erreur non classée : lire les logs `mail.failed` du back.",
};

/** What to do next, or null when everything passed. */
export function outcomeHint(outcome: string): string | null {
  return OUTCOME_HINTS[outcome] ?? null;
}

export type FingerprintComparison = "same" | "different" | "unknown";

export function compareFingerprints(fp: KeyFingerprints): FingerprintComparison {
  if (fp.same_key === true) return "same";
  if (fp.same_key === false) return "different";
  return "unknown";
}

export const FINGERPRINT_COMPARISON_LABEL: Record<FingerprintComparison, string> = {
  same: "Même clé dans SMTP_PASSWORD et SENDGRID_API_KEY",
  different: "Clés différentes dans SMTP_PASSWORD et SENDGRID_API_KEY",
  unknown: "Comparaison impossible : une des deux clés n'est pas configurée",
};

export type Tone = "ok" | "warning" | "error" | "neutral";

export const TONE_CLASS: Record<Tone, string> = {
  ok: "text-green-700",
  warning: "text-orange-700",
  error: "text-red-700",
  neutral: "text-gray-600",
};

const SENDGRID_ERRORS: Record<string, string> = {
  timeout: "SendGrid n'a pas répondu à temps",
  unreachable: "API SendGrid injoignable",
  forbidden: "accès refusé (403)",
  invalid_response: "réponse illisible",
};

/** `valid: null` is « could not tell » — never shown as an invalid key. */
export function sendgridKeySummary(status: SendgridKeyStatus): { label: string; tone: Tone } {
  if (!status.configured) {
    return { label: "Non configurée (SENDGRID_API_KEY vide)", tone: "neutral" };
  }
  if (status.valid === true) {
    return status.has_mail_send
      ? {
          label: `Valide — permission mail.send présente (${status.scopes_count ?? "?"} permissions)`,
          tone: "ok",
        }
      : { label: "Valide, mais sans la permission mail.send", tone: "warning" };
  }
  if (status.valid === false) {
    return { label: "Refusée par SendGrid (401) : clé révoquée ou fausse", tone: "error" };
  }
  const reason =
    (status.error ? SENDGRID_ERRORS[status.error] : undefined) ||
    (status.http_status ? `réponse inattendue (${status.http_status})` : "cause inconnue");
  return { label: `Statut inconnu — ${reason}`, tone: "warning" };
}

export function transportLabel(smtp: Pick<SmtpConfig, "use_ssl" | "use_starttls">): string {
  if (smtp.use_ssl) return "SMTPS (TLS implicite)";
  if (smtp.use_starttls) return "SMTP + STARTTLS";
  return "SMTP sans TLS";
}

export function formatDuration(ms: number | null): string {
  return ms == null ? "—" : `${ms} ms`;
}
